/**
 * GS1 GTIN-14 and UPC-12 Validation Utilities
 */

import {
  ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS,
  PRODUCT_IMAGERY_LABELS_BY_LEVEL,
} from 'common/components/product-add/components/constants';
import { lowercaseFirstLetter } from 'pages/product-full-view/shared/utils';

/**
 * Calculate the check digit for GTIN (UPC/EAN standard).
 * @param {string} number - Number without check digit
 * @returns {number} - Check digit (0-9)
 */
const calculateCheckDigit = (number) => {
  let sum = 0;
  let isOdd = true; // Start from the rightmost digit

  for (let i = number.length - 1; i >= 0; i--) {
    const digit = number.charCodeAt(i) - 48; // Convert char to number
    sum += isOdd ? digit * 3 : digit;
    isOdd = !isOdd;
  }

  const mod = sum % 10;
  return mod === 0 ? 0 : 10 - mod;
};

/**
 * Validate check digit for any GTIN format
 * @param {string} input - Full GTIN including check digit
 * @returns {boolean} - True if valid
 */
export const isValidCheckDigit = (input) => {
  // Convert to string first
  const inputStr = String(input);

  if (!inputStr || inputStr.length === 0) return false;

  // Remove any non-digit characters
  const cleanInput = inputStr.replace(/\D/g, '');

  if (!cleanInput || cleanInput.length === 0) return false;

  // Extract check digit (last character)
  const checkDigit = cleanInput.charCodeAt(cleanInput.length - 1) - 48;
  const calculated = calculateCheckDigit(
    cleanInput.substring(0, cleanInput.length - 1)
  );

  return checkDigit === calculated;
};
/**
 * Validate GTIN-14 format and check digit
 * @param {string} gtin - GTIN-14 to validate
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateGTIN = (gtin, fieldLabel = 'GTIN') => {
  if (!gtin) {
    return { isValid: true, error: null }; // Let Form.Item required rule handle this
  }

  // Check if it's numeric
  if (!/^\d+$/.test(gtin)) {
    return { isValid: false, error: `${fieldLabel} must contain digits only.` };
  }

  // Remove spaces and non-numeric characters
  const cleanGtin = gtin.replace(/\D/g, '');

  // Check if it's exactly 14 digits
  if (cleanGtin.length !== 14) {
    return {
      isValid: false,
      error: `${fieldLabel} must be exactly 14 digits (current: ${cleanGtin.length})`,
    };
  }

  // Validate check digit using GS1 algorithm
  if (!isValidCheckDigit(cleanGtin)) {
    return {
      isValid: false,
      error: `${fieldLabel} check digit is invalid.`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Format GTIN-14 with proper spacing
 * @param {string} gtin - GTIN-14 to format
 * @returns {string} - Formatted GTIN
 */
export const formatGTIN = (gtin) => {
  if (!gtin) return '';
  const cleanGtin = gtin.replace(/\D/g, '');

  if (cleanGtin.length === 14) {
    return cleanGtin.replace(/(\d{2})(\d{6})(\d{6})/, '$1 $2 $3');
  }

  return cleanGtin;
};

/**
 * Validate UPC-12 format and check digit
 * @param {string} upc - UPC-12 to validate
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateUPC12 = (upc) => {
  if (!upc) {
    return { isValid: true, error: null }; // UPC-12 is optional
  }

  // Check if it's numeric
  if (!/^\d+$/.test(upc)) {
    return { isValid: false, error: 'UPC-12 must contain digits only.' };
  }

  // Remove spaces and non-numeric characters
  const cleanUpc = upc.replace(/\D/g, '');

  // Check if it's exactly 12 digits
  if (cleanUpc.length !== 12) {
    return {
      isValid: false,
      error: `UPC-12 must be exactly 12 digits (current: ${cleanUpc.length})`,
    };
  }

  // Validate check digit
  if (!isValidCheckDigit(cleanUpc)) {
    return {
      isValid: false,
      error: 'UPC-12 check digit is invalid.',
    };
  }

  return { isValid: true, error: null };
};

/**
 * Format UPC-12 with proper spacing
 * @param {string} upc - UPC-12 to format
 * @returns {string} - Formatted UPC-12
 */
export const formatUPC12 = (upc) => {
  if (!upc) return '';
  const cleanUpc = upc.replace(/\D/g, '');

  if (cleanUpc.length === 12) {
    // Format as: X-XXXXX-XXXXX-X
    return cleanUpc.replace(/(\d{1})(\d{5})(\d{5})(\d{1})/, '$1-$2-$3-$4');
  }

  return cleanUpc;
};

export const clearValidatedFieldErrors = (form) => {
  // Fields to keep errors for
  const fieldsToKeepErrors = [
    'productName',
    'packageLevel',
    'gtin',
    'upc12',
    'upc13',
    'upc8',
  ];

  // Get all fields with errors from the form
  const errors = form.getFieldsError();

  // Find fields that have errors and should be cleared
  const fieldsToClear = errors
    .filter((field) => field.errors && field.errors.length > 0) // Has errors
    .filter((field) => !fieldsToKeepErrors.includes(field.name[0])) // Not in keep list
    .map((field) => ({
      name: field.name[0],
      errors: [],
    }));

  // Clear errors for those fields
  if (fieldsToClear.length > 0) {
    form.setFields(fieldsToClear);
  }
};

export const isUnitPackageLevel = (value) => {
  return value?.toLowerCase() === 'unit';
};

export const checkActiveStatus = (value) => {
  return value?.toLowerCase() === 'active';
};

export const checkRequiredPrimaryFieldForm = ({ isActiveStatus, isUnit }) => {
  return isActiveStatus && isUnit;
};

/**
 * F01.01: Non-standard, high-ASCII characters validation
 * Only allows standard ASCII printable characters (0x20-0x7E) plus newline, carriage return, and tab
 */
export const getHighAsciiValidationRule = () => ({
  pattern: /^[\x20-\x7E\n\r\t]*$/,
  message: 'Non-standard, high-ASCII characters are not allowed',
});

/**
 * F01.02: Trademark symbols validation
 * Disallows ®, ™, and SM symbols
 */
export const getTrademarkValidationRule = () => ({
  pattern: /^(?!.*[®™℠]).*$/s,
  message: 'Trademark symbols (®, ™, ℠) are not allowed',
});

/**
 * F01.03: Special characters validation for Product Name and Brand
 * Only allows: % ' " & - , . ! ( ) + / É Ñ é ñ (and alphanumeric + spaces)
 * Disallows: [ ] / \ | * and other special characters
 */

export const getSpecialCharsRule = (displayFieldName) => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve();

    // Allowed: letters, numbers, spaces, %, quotes, curly quotes,
    // &, -, comma, period, !, (), +, /, É, Ñ, é, ñ
    const invalidChars = [
      ...new Set(
        value
          .split('')
          .filter((char) => !/[a-zA-Z0-9\s%'"‘’“”&\-,.!()+/ÉÑéñ]/.test(char))
      ),
    ];

    if (invalidChars.length > 0) {
      return Promise.reject(
        new Error(
          `${displayFieldName} contains invalid characters: ${invalidChars.join(
            ', '
          )}`
        )
      );
    }

    return Promise.resolve();
  },
});

export const getHighAsciiValidationRuleForRestricted = () => ({
  pattern: /^[\x20-\x7E\n\r\t\u2018\u2019\u201C\u201D]*$/, // allow curly quotes
  message: 'Non-standard, high-ASCII characters are not allowed',
});

export const getHighAsciiValidationRuleWithAccents = () => ({
  pattern: /^[\x20-\x7E\n\r\t\u2018\u2019\u201C\u201DÉéÑñ()+/]*$/,
  message: 'Non-standard, high-ASCII characters are not allowed',
});
/**
 * Get validation rules for Product Name field
 * Applies all three rules: F01.01, F01.02, F01.03
 */
export const getRestrictedTextValidationRules = (displayFieldName) => [
  getHighAsciiValidationRuleWithAccents(),
  getTrademarkValidationRule(),
  getSpecialCharsRule(displayFieldName),
];

/* Same as getRestrictedTextValidationRules, but without getSpecialCharsRule */
export const getProductDescriptionValidationRules = () => [
  getHighAsciiValidationRuleWithAccents(),
  getTrademarkValidationRule(),
];

/**
 * Get validation rules for other text fields (not Product Name/Brand)
 * Applies only F01.01 and F01.02
 */
export const getStandardTextValidationRules = () => [
  getHighAsciiValidationRule(),
  getTrademarkValidationRule(),
];

/**
 * Sanitize Product Name by removing invalid characters
 * Applies all three rules: F01.01, F01.02, F01.03
 * @param {string} value - The product name to sanitize
 * @returns {string} - Sanitized product name
 */
export const sanitizeProductName = (value) => {
  if (!value) return '';

  let sanitized = value;

  // F01.02: Remove trademark symbols (®, ™, ℠)
  sanitized = sanitized.replace(/[®™℠]/g, '');

  // F01.01: Remove non-standard, high-ASCII characters
  // Keep only standard ASCII printable characters (0x20-0x7E) plus newline, carriage return, and tab
  sanitized = sanitized.replace(/[^\x20-\x7E\n\r\t]/g, '');

  // F01.03: Remove special characters not in the allowed list
  // Keep only: alphanumeric, spaces, and % ' " & - , . !
  sanitized = sanitized.replace(/[^a-zA-Z0-9\s%'"&\-,.!]/g, '');

  // Clean up multiple spaces
  sanitized = sanitized.replace(/\s+/g, ' ').trim();

  return sanitized;
};

/**
 * Sanitize standard text fields (not Product Name)
 * Applies only F01.01 and F01.02
 * @param {string} value - The text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeStandardText = (value) => {
  if (!value) return '';

  let sanitized = value;

  // F01.02: Remove trademark symbols (®, ™, ℠)
  sanitized = sanitized.replace(/[®™℠]/g, '');

  // F01.01: Remove non-standard, high-ASCII characters
  // Keep only standard ASCII printable characters (0x20-0x7E) plus newline, carriage return, and tab
  sanitized = sanitized.replace(/[^\x20-\x7E\n\r\t]/g, '');

  return sanitized;
};

/**
 * Check if a value has validation errors according to standard text rules
 * @param {string} value - The value to check
 * @returns {boolean} - True if there are errors
 */
export const hasStandardTextErrors = (value) => {
  if (!value) return false;

  const rules = getStandardTextValidationRules();

  for (const rule of rules) {
    if (rule.pattern && !rule.pattern.test(value)) {
      return true;
    }
  }

  return false;
};

/**
 * F01.03 - Product Name and Brand Validation
 *
 * Brackets, symbols, slashes, asterisks, etc, will be removed from the Product Name and Brand fields.
 * The symbols which are allowed are: % ' " & - , . !
 *
 * This file contains validation rules and utilities for validating Product Name and Brand fields
 * to ensure only allowed special characters are used.
 */

/**
 * Allowed special characters for Product Name and Brand fields
 * Characters: % ' " & - , . !
 */
export const ALLOWED_SPECIAL_CHARACTERS = /[%'"&\-,.!]/;

/**
 * Regular expression to match any character that is NOT:
 * - Alphanumeric (letters and numbers)
 * - Whitespace
 * - Allowed special characters: % ' " & - , . !
 *
 * This regex will match INVALID characters that should not be allowed
 */
export const INVALID_CHARACTERS_REGEX = /[^a-zA-Z0-9\s%'"&\-,.!]/;

/**
 * Regular expression to match all invalid characters globally
 * Used for removing/replacing invalid characters
 */
export const INVALID_CHARACTERS_GLOBAL_REGEX = /[^a-zA-Z0-9\s%'"&\-,.!]/g;

/**
 * Validation function for Ant Design Form.Item rules
 *
 * @returns {Object} Ant Design validation rule object
 */
export const validateProductNameBrandCharacters = () => ({
  validator: (_, value) => {
    if (!value) {
      // Allow empty values - use 'required' rule separately if needed
      return Promise.resolve();
    }

    const stringValue = String(value).trim();

    if (!stringValue) {
      return Promise.resolve();
    }

    // Check if the value contains any invalid characters
    if (INVALID_CHARACTERS_REGEX.test(stringValue)) {
      // Extract invalid characters for a more helpful error message
      const invalidChars = stringValue.match(INVALID_CHARACTERS_GLOBAL_REGEX);
      const uniqueInvalidChars = [...new Set(invalidChars)].join(', ');

      return Promise.reject(
        new Error(`Invalid characters detected: ${uniqueInvalidChars}`)
      );
    }

    return Promise.resolve();
  },
});

export const mapImageryValidationErrors = (errors, packageLevel) => {
  // Handle invalid input
  if (!Array.isArray(errors)) {
    return {};
  }

  const errorMap = {};

  for (let i = 0; i < errors.length; i++) {
    const error = errors[i];

    if (!error?.fieldName) {
      continue;
    }

    const mappedFieldName =
      ACTIVE_PRODUCT_VALIDATE_IMAGERY_ERRORS[
        lowercaseFirstLetter(error.fieldName)
      ];

    // Only include errors that have a valid mapping
    if (mappedFieldName) {
      const label =
        PRODUCT_IMAGERY_LABELS_BY_LEVEL[packageLevel][mappedFieldName];

      errorMap[mappedFieldName] = {
        errorMessage: error.errorMessage,
        label,
      };
    }
  }

  return errorMap;
};

export const checkRequiredPrimaryFieldForActiveProduct = (
  activeResult,
  fieldName
) => {
  const primaryErrors = activeResult?.errors ?? [];
  const errorFieldNames = primaryErrors.map((error) =>
    error?.fieldName?.toLowerCase()
  );
  return errorFieldNames.includes(fieldName?.toLowerCase());
};

/**
 * Pad a string/number with zeros on the left
 * @param {string|number} str - The value to pad
 * @param {number} totalLength - The desired total length
 * @param {string} padChar - The character to pad with (default '0')
 * @returns {string} - Padded string
 */
const padLeft = (str, totalLength, padChar) => {
  str = String(str);
  padChar = padChar || '0';

  if (str.length >= totalLength) {
    return str;
  }

  const padLength = totalLength - str.length;
  let padding = '';
  for (let i = 0; i < padLength; i++) {
    padding += padChar;
  }

  return padding + str;
};

/**
 * Convert UPC-12 to GTIN-14
 * @param {string|number} upc12 - UPC-12 code (12 digits)
 * @returns {string|null} - GTIN-14 code or null if invalid
 */
export const upc12ToGTIN14 = (upc12) => {
  if (!upc12) return null;

  // Clean the input (remove any non-digit characters)
  const cleanUpc = String(upc12).replace(/\D/g, '');

  // Validate length
  if (cleanUpc.length !== 12) return null;

  // Pad with 2 zeros on the left to make 14 digits
  return padLeft(cleanUpc, 14, '0');
};

/* Convert GTIN-14 to UPC-12
 * @param {string|number} gtin14 - GTIN-14 code (14 digits)
 * @returns {string|null} - UPC-12 code or null if invalid
 * Note: This only works for GTINs that were originally UPC-12 codes
 * (i.e., GTINs that start with "00")
 */
export const gtinToUpc12 = (gtin14) => {
  if (!gtin14) return null;

  // Clean the input (remove any non-digit characters)
  const cleanGtin = String(gtin14).replace(/\D/g, '');

  // Validate length
  if (cleanGtin.length !== 14) return null;

  // Extract the potential UPC-12 (remove first 2 digits)
  const upc12 = cleanGtin.substring(2);

  // Validate that padding this UPC-12 back would give us the original GTIN
  // (i.e., verify it was originally a UPC-12 padded with "00")
  if (padLeft(upc12, 14, '0') !== cleanGtin) return null;

  return upc12;
};
/**
 * Expand UPC-E to UPC-A (UPC-12) using backend logic
 * Matches backend C# UpcHelper.ConvertUpc8ToUpc12
 * @param {string|number} upce - UPC-E code (8 digits)
 * @returns {string|null} - UPC-A (UPC-12) code or null if invalid
 */
export const upceToUPC12 = (upce) => {
  if (!upce) return null;

  const upceStr = String(upce).replace(/\D/g, '');

  if (upceStr.length !== 8) return null;

  const ns = upceStr[0];
  const body = upceStr.slice(1, 7); // 6 characters
  const upc8CheckDigit = parseInt(upceStr[7], 10);
  const patternDigit = upceStr[6]; // Same as body[5]

  let upc11;
  switch (patternDigit) {
    case '0':
    case '1':
    case '2':
      upc11 = `${ns}${body[0]}${body[1]}${body[5]}0000${body[2]}${body[3]}${body[4]}`;
      break;
    case '3':
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}00000${body[3]}${body[4]}`;
      break;
    case '4':
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}${body[3]}00000${body[4]}`;
      break;
    default: // 5-9
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}${body[3]}${body[4]}0000${body[5]}`;
      break;
  }

  const calculatedCheckDigit = calculateCheckDigit(upc11);

  // Compare check digits
  if (upc8CheckDigit !== calculatedCheckDigit) {
    return null;
  }

  return `${upc11}${calculatedCheckDigit}`;
};

/**
 * Expand UPC-E to GTIN-14
 * @param {string|number} upce - UPC-E code (8 digits)
 * @returns {string|null} - GTIN-14 code or null if invalid
 */
export const upceToGTIN14 = (upce) => {
  const upca = upceToUPC12(upce);
  if (!upca) return null;

  // Convert UPC-A to GTIN-14 by padding with 2 zeros
  return upc12ToGTIN14(upca);
};

/**
 * Validate UPC-E (UPC-8) format and check digit
 * Matches backend C# UpcHelper.ConvertUpc8ToUpc12 logic
 * @param {string} upce - UPC-E to validate (8 digits)
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateUPCE = (upce) => {
  if (!upce) {
    return { isValid: true, error: null };
  }

  if (!/^\d+$/.test(upce)) {
    return { isValid: false, error: 'UPC-8 must contain digits only.' };
  }

  const cleanUpce = upce.replace(/\D/g, '');

  if (cleanUpce.length !== 8) {
    return {
      isValid: false,
      error: `UPC-8 must be exactly 8 digits (current: ${cleanUpce.length})`,
    };
  }

  const ns = cleanUpce[0]; // Number System

  if (ns !== '0' && ns !== '1') {
    return {
      isValid: false,
      error: 'UPC-8 must begin with 0 or 1.',
    };
  }

  const body = cleanUpce.slice(1, 7); // 6 characters (body[0] to body[5])
  const upc8CheckDigit = parseInt(cleanUpce[7], 10);
  const patternDigit = cleanUpce[6]; // Same as body[5]

  let upc11;
  switch (patternDigit) {
    case '0':
    case '1':
    case '2':
      // Backend: $"{ns}{body[0]}{body[1]}{body[5]}0000{body[2]}{body[3]}{body[4]}"
      upc11 = `${ns}${body[0]}${body[1]}${body[5]}0000${body[2]}${body[3]}${body[4]}`;
      break;
    case '3':
      // Backend: $"{ns}{body[0]}{body[1]}{body[2]}00000{body[3]}{body[4]}"
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}00000${body[3]}${body[4]}`;
      break;
    case '4':
      // Backend: $"{ns}{body[0]}{body[1]}{body[2]}{body[3]}00000{body[4]}"
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}${body[3]}00000${body[4]}`;
      break;
    default: // 5-9
      // Backend: $"{ns}{body[0]}{body[1]}{body[2]}{body[3]}{body[4]}0000{body[5]}"
      upc11 = `${ns}${body[0]}${body[1]}${body[2]}${body[3]}${body[4]}0000${body[5]}`;
      break;
  }

  const calculatedCheckDigit = calculateCheckDigit(upc11);

  if (upc8CheckDigit !== calculatedCheckDigit) {
    return {
      isValid: false,
      error: 'UPC-8 check digit is invalid.',
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validate UPC-13 format and check digit
 * @param {string} upc13 - UPC-13 to validate
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateUPC13 = (upc13) => {
  if (!upc13) {
    return { isValid: true, error: null }; // UPC-13 is optional
  }

  // Check if it's numeric
  if (!/^\d+$/.test(upc13)) {
    return { isValid: false, error: 'UPC-13 must contain digits only.' };
  }

  // Remove spaces and non-numeric characters
  const cleanUpc13 = upc13.replace(/\D/g, '');

  // Check if it's exactly 13 digits
  if (cleanUpc13.length !== 13) {
    return {
      isValid: false,
      error: `UPC-13 must be exactly 13 digits (current: ${cleanUpc13.length})`,
    };
  }

  // Validate check digit
  if (!isValidCheckDigit(cleanUpc13)) {
    return {
      isValid: false,
      error: 'UPC-13 check digit is invalid.',
    };
  }

  return { isValid: true, error: null };
};

/**
 * Convert UPC-13 to UPC-12 by removing leading zero
 * @param {string|number} upc13 - UPC-13 code (13 digits)
 * @returns {string|null} - UPC-12 code or null if invalid
 */
export const upc13ToUpc12 = (upc13) => {
  if (!upc13) return null;

  const cleanUpc13 = String(upc13).replace(/\D/g, '');

  // Validate length
  if (cleanUpc13.length !== 13) return null;

  // Only convert if it starts with 0
  if (!cleanUpc13.startsWith('0')) return null;

  // Remove leading zero
  return cleanUpc13.substring(1);
};

/**
 * Convert UPC-12 to UPC-13 by adding leading zero
 * @param {string|number} upc12 - UPC-12 code (12 digits)
 * @returns {string|null} - UPC-13 code or null if invalid
 */
export const upc12ToUpc13 = (upc12) => {
  if (!upc12) return null;

  const cleanUpc12 = String(upc12).replace(/\D/g, '');

  // Validate length
  if (cleanUpc12.length !== 12) return null;

  // Add leading zero
  return '0' + cleanUpc12;
};

/**
 * Convert GTIN-14 to UPC-13 by removing one leading zero
 * @param {string|number} gtin14 - GTIN-14 code (14 digits)
 * @returns {string|null} - UPC-13 code or null if invalid
 */
export const gtinToUpc13 = (gtin14) => {
  if (!gtin14) return null;

  const cleanGtin = String(gtin14).replace(/\D/g, '');

  // Validate length
  if (cleanGtin.length !== 14) return null;

  // Only convert if it starts with single 0
  if (!cleanGtin.startsWith('0')) return null;

  // Remove first digit
  return cleanGtin.substring(1);
};

/**
 * Convert UPC-13 to GTIN-14 by adding leading zero
 * @param {string|number} upc13 - UPC-13 code (13 digits)
 * @returns {string|null} - GTIN-14 code or null if invalid
 */
export const upc13ToGtin = (upc13) => {
  if (!upc13) return null;

  const cleanUpc13 = String(upc13).replace(/\D/g, '');

  // Validate length
  if (cleanUpc13.length !== 13) return null;

  // Add leading zero
  return '0' + cleanUpc13;
};

export const normalizeNumericWithMaxLength = (value, maxLength) => {
  if (!value) return value;
  return value
    .replace(/\D/g, '') // digits only
    .slice(0, maxLength); // enforce length HERE
};

export const validateUnitUpc = (upc, fieldLabel = 'UPC-12') => {
  if (!upc) return { isValid: true, error: null };

  if (!/^\d+$/.test(upc)) {
    return { isValid: false, error: `${fieldLabel} must contain digits only.` };
  }

  const cleanUpc = upc.replace(/\D/g, '');

  if (cleanUpc.length !== 12 && cleanUpc.length !== 13) {
    return {
      isValid: false,
      error: `${fieldLabel} must be exactly 12 or 13 digits (current: ${cleanUpc.length})`,
    };
  }

  if (!isValidCheckDigit(cleanUpc)) {
    return { isValid: false, error: `${fieldLabel} check digit is invalid` };
  }

  return { isValid: true, error: null };
};

export const createUpc12Validator =
  (fieldLabel = 'UPC-12') =>
  (_, value) => {
    if (!value) return Promise.resolve();
    const validation = validateUnitUpc(value, fieldLabel);
    if (!validation.isValid) {
      return Promise.reject(new Error(validation.error));
    }
    return Promise.resolve();
  };

export const createGtinValidator =
  (fieldLabel = 'GTIN') =>
  (_, value) => {
    if (!value) return Promise.resolve(undefined);
    const validation = validateGTIN(value, fieldLabel);
    if (!validation.isValid) {
      return Promise.reject(new Error(validation.error));
    }
    return Promise.resolve();
  };

export const productValidationFormItemTypes = {
  gtin: {
    validator: (_, value) => {
      if (!value) return Promise.resolve(undefined);
      const validation = validateGTIN(value);
      if (!validation.isValid) {
        return Promise.reject(new Error(validation.error));
      }
      return Promise.resolve();
    },
  },

  upc12: {
    validator: (_, value) => {
      if (!value) {
        return Promise.resolve();
      }
      const validation = validateUnitUpc(value);
      if (!validation.isValid) {
        return Promise.reject(new Error(validation.error));
      }
      return Promise.resolve();
    },
  },
};

export const padWithZeros = (value, length) => {
  const str = String(value);
  const zerosNeeded = length - str.length;
  return zerosNeeded > 0 ? '0'.repeat(zerosNeeded) + str : str;
};
